// src/lib/compare-store.ts
// -----------------------------------------------------------------------------
// A tiny localStorage-backed "compare list" of unit codes (max 3), synced
// across components via a custom event. Deterministic empty initial state
// avoids hydration mismatch; the stored list loads after mount.
// -----------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";

const KEY = "tahawer-compare";
const EVENT = "tahawer-compare-change";
const MAX = 3;

function read(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(v) ? v.slice(0, MAX) : [];
  } catch {
    return [];
  }
}

function write(codes: string[]) {
  localStorage.setItem(KEY, JSON.stringify(codes));
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function useCompare() {
  const [codes, setCodes] = useState<string[]>([]);

  useEffect(() => {
    const sync = () => setCodes(read());
    sync();
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const add = useCallback((code: string) => {
    const cur = read();
    if (!cur.includes(code) && cur.length < MAX) write([...cur, code]);
  }, []);

  const remove = useCallback((code: string) => {
    write(read().filter((c) => c !== code));
  }, []);

  const clear = useCallback(() => write([]), []);

  return { codes, add, remove, clear, has: (c: string) => codes.includes(c), full: codes.length >= MAX };
}
